import { useEffect, useState } from 'react';
import { type Session } from '@supabase/supabase-js';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarTrigger,
} from './components/ui/sidebar';
import {
  LayoutDashboard,
  Package,
  BarChart3,
  Sparkles,
  Settings as SettingsIcon,
  LogOut,
  ChevronDown,
  DollarSign,
  Shield,
} from 'lucide-react';
import { DashboardPage } from './pages/DashboardPage';
import { KnowledgeBasePage } from './pages/KnowledgeBasePage';
import { Settings } from './pages/Settings';
import { ProductManagementPage } from './pages/ProductManagementPage';
import { ReportPage } from './pages/ReportPage';
import { CarbCuttReportsPage } from './pages/CarbCuttReportsPage';
import { GeneralAffiliateReportsPage } from './pages/GeneralAffiliateReportsPage';
import { AffiliatePayOutPage } from './pages/AffiliatePayOutPage';
import { BrandManagementPage } from './pages/BrandManagementPage';
import { MBQManagementPage } from './pages/MBQManagementPage';
import { GiftCardEventsPage } from './pages/GiftCardEventsPage';
import { FreeGiftCostPage } from './pages/FreeGiftCostPage';
import { CustomerEarnPage } from './pages/CustomerEarnPage';
import { EarnTransactionPage } from './pages/EarnTransactionPage';
import { EarnPointsPage } from './pages/EarnPointsPage';
import { EarnAnalyticsPage } from './pages/EarnAnalyticsPage';
import { GrossProfitPage } from './pages/GrossProfitPage';
import { VIPAuditPage } from './pages/VIPAuditPage';
import { VIPLoggerPage } from './pages/VIPLoggerPage';
import { GameMasterAnalyticaPage } from './pages/GameMasterAnalyticaPage';
import { FashionDesignPredictionPage } from './pages/FashionDesignPredictionPage';
import { Avatar, AvatarFallback } from './components/ui/avatar';
import { Button } from './components/ui/button';
import { SystemErrorAlert } from './components/SystemErrorAlert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './components/ui/collapsible';
import { LoginPage } from './components/auth/LoginPage';
import { getCurrentSession, onAuthStateChange, signInWithPassword, signOutUser } from './lib/supabaseAuth';
import { cn } from './lib/utils';

function getUserNameFromEmail(email: string): string {
  const [namePart] = email.split('@');
  return namePart || email;
}

function getInitials(value: string): string {
  const text = value.trim();
  if (!text) {
    return 'AV';
  }
  return text.slice(0, 2).toUpperCase();
}

export default function App() {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [dashboardOpen, setDashboardOpen] = useState(true);
  const [productsOpen, setProductsOpen] = useState(false);
  const [earnOpen, setEarnOpen] = useState(false);
  const [vipCentreOpen, setVipCentreOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    getCurrentSession()
      .then((currentSession) => {
        if (isMounted) {
          setSession(currentSession);
        }
      })
      .finally(() => {
        if (isMounted) {
          setAuthLoading(false);
        }
      });

    const { data } = onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });

    return () => {
      isMounted = false;
      data.subscription.unsubscribe();
    };
  }, []);

  async function handleLogin(email: string, password: string) {
    await signInWithPassword(email, password);
  }

  async function handleLogout() {
    await signOutUser();
    setActiveSection('dashboard');
  }

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40">
        <p className="text-sm text-muted-foreground">Checking session...</p>
      </div>
    );
  }

  if (!session) {
    return <LoginPage onLogin={handleLogin} />;
  }

  const userEmail = session.user.email ?? 'unknown@example.com';
  const userName = getUserNameFromEmail(userEmail);
  const userInitials = getInitials(userName);

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full overflow-hidden">
        {/* Sidebar */}
        <Sidebar>
          <SidebarHeader className="border-b px-6 py-4">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-black dark:bg-white flex items-center justify-center overflow-hidden">
                <img
                  src="/logo-white.png"
                  alt="Ample Vantage logo"
                  className="block dark:hidden h-6 w-6 object-contain"
                />
                <img
                  src="/logo-black.png"
                  alt="Ample Vantage logo"
                  className="hidden dark:block h-6 w-6 object-contain"
                />
              </div>
              <span className="font-semibold text-sidebar-foreground">Ample Vantage</span>
            </div>
          </SidebarHeader>

          <SidebarContent>
            <SidebarGroup>
              <SidebarMenu>
                {/* Dashboard */}
                <Collapsible open={dashboardOpen} onOpenChange={setDashboardOpen}>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => setActiveSection('dashboard')}
                      isActive={activeSection === 'dashboard'}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      <span>Dashboard</span>
                      <ChevronDown
                        className={`ml-auto h-4 w-4 transition-transform ${
                          dashboardOpen ? 'rotate-180' : ''
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDashboardOpen(!dashboardOpen);
                        }}
                      />
                    </SidebarMenuButton>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('gross-profit')}
                            isActive={activeSection === 'gross-profit'}
                          >
                            Gross Profit
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('earn-analytics')}
                            isActive={activeSection === 'earn-analytics'}
                          >
                            Earn Analytics
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('vip-audit')}
                            isActive={activeSection === 'vip-audit'}
                          >
                            VIP Audit
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>

                {/* Products */}
                <Collapsible open={productsOpen} onOpenChange={setProductsOpen}>
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton>
                        <Package className="h-4 w-4" />
                        <span>Data Hubs</span>
                        <ChevronDown
                          className={`ml-auto h-4 w-4 transition-transform ${
                            productsOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('product-management')}
                            isActive={activeSection === 'product-management'}
                          >
                            Product Management
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('brand-management')}
                            isActive={activeSection === 'brand-management'}
                          >
                            Brand Management
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('mbq-management')}
                            isActive={activeSection === 'mbq-management'}
                          >
                            MBQ Management
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('gift-card-events')}
                            isActive={activeSection === 'gift-card-events'}
                          >
                            Gift Management
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('free-gift-costs')}
                            isActive={activeSection === 'free-gift-costs'}
                          >
                            Free Gift Cost
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => setActiveSection('ai-hub-fashion-design-prediction')}
                    isActive={activeSection === 'ai-hub-fashion-design-prediction'}
                  >
                    <Sparkles className="h-4 w-4" />
                    <span>AI Hub</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                <Collapsible open={earnOpen} onOpenChange={setEarnOpen}>
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton>
                        <DollarSign className="h-4 w-4" />
                        <span>Earn</span>
                        <ChevronDown
                          className={`ml-auto h-4 w-4 transition-transform ${
                            earnOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('overall-records')}
                            isActive={activeSection === 'overall-records'}
                          >
                            Customer Earn
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('earn-transaction')}
                            isActive={activeSection === 'earn-transaction'}
                          >
                            Earn Transaction
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('earn-points')}
                            isActive={activeSection === 'earn-points'}
                          >
                            Earn Points
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>

                {/* VIP Centre */}
                <Collapsible open={vipCentreOpen} onOpenChange={setVipCentreOpen}>
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton>
                        <Shield className="h-4 w-4" />
                        <span>VIP Centre</span>
                        <ChevronDown
                          className={`ml-auto h-4 w-4 transition-transform ${
                            vipCentreOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('vip-logger')}
                            isActive={activeSection === 'vip-logger'}
                          >
                            VIP Logger
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('game-master-analytica')}
                            isActive={activeSection === 'game-master-analytica'}
                          >
                            Game Master Analytica
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>

                {/* Report */}
                <Collapsible open={reportOpen} onOpenChange={setReportOpen}>
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton>
                        <BarChart3 className="h-4 w-4" />
                        <span>Report</span>
                        <ChevronDown
                          className={`ml-auto h-4 w-4 transition-transform ${
                            reportOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('report')}
                            isActive={activeSection === 'report'}
                          >
                            Sales Report
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('report-carb-cutt')}
                            isActive={activeSection === 'report-carb-cutt'}
                          >
                            Carb Cutt Reports
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('report-general-affiliate')}
                            isActive={activeSection === 'report-general-affiliate'}
                          >
                            General Affiliate Reports
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => setActiveSection('report-affiliate-payout')}
                            isActive={activeSection === 'report-affiliate-payout'}
                          >
                            Affiliate Pay-Out
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              </SidebarMenu>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter className="border-t p-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start gap-3 px-2 h-auto py-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium truncate">{userName}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {userEmail}
                    </p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setActiveSection('settings')}>
                  <SettingsIcon className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarFooter>
        </Sidebar>

        {/* Main Content */}
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="border-b bg-background">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-4">
                <SidebarTrigger />
                <div>
                  <h1 className="text-2xl font-semibold">
                    {activeSection === 'dashboard' && 'Dashboard'}
                    {activeSection === 'gross-profit' && 'Gross Profit'}
                    {activeSection === 'earn-analytics' && 'Earn Analytics'}
                    {activeSection === 'vip-audit' && 'VIP Audit'}
                    {activeSection === 'vip-logger' && 'VIP Logger'}
                    {activeSection === 'game-master-analytica' && 'Game Master Analytica'}
                    {activeSection === 'product-management' && 'Product Management'}
                    {activeSection === 'brand-management' && 'Brand Management'}
                    {activeSection === 'mbq-management' && 'MBQ Management'}
                    {activeSection === 'gift-card-events' && 'Gift Card Events'}
                    {activeSection === 'free-gift-costs' && 'Free Gift Cost'}
                    {activeSection === 'ai-hub-fashion-design-prediction' && 'Fashion Design Prediction'}
                    {activeSection === 'overall-records' && 'Customer Earn'}
                    {activeSection === 'earn-transaction' && 'Earn Transaction'}
                    {activeSection === 'earn-points' && 'Earn Points'}
                    {activeSection === 'report' && 'Reports'}
                    {activeSection === 'report-carb-cutt' && 'Carb Cutt Reports'}
                    {activeSection === 'report-general-affiliate' && 'General Affiliate Reports'}
                    {activeSection === 'report-affiliate-payout' && 'Affiliate Pay-Out'}
                    {activeSection === 'knowledge-base' && 'Knowledge Base'}
                    {activeSection === 'settings' && 'Settings'}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {activeSection === 'dashboard' && 'Overview of your business metrics'}
                    {activeSection === 'gross-profit' && 'Analyze your profit margins'}
                    {activeSection === 'earn-analytics' && 'Monitor your revenue streams'}
                    {activeSection === 'vip-audit' && 'Visual audit insights with timeframe comparison'}
                    {activeSection === 'vip-logger' && 'Technical audit metrics and drill-down tools'}
                    {activeSection === 'game-master-analytica' && 'Mission engagement and month-over-month insights'}
                    {activeSection === 'product-management' && 'Manage your product inventory and variants'}
                    {activeSection === 'brand-management' && 'Manage your brands and categories'}
                    {activeSection === 'mbq-management' && 'Manage margin-by-quantity calculation groups'}
                    {activeSection === 'gift-card-events' && 'View and manage gift card promotions'}
                    {activeSection === 'free-gift-costs' && 'Manage date-versioned free gift cost overrides'}
                    {activeSection === 'ai-hub-fashion-design-prediction' && 'Upload product inputs and run asynchronous fashion demand share predictions'}
                    {activeSection === 'overall-records' && 'View complete overview of all customer earn records'}
                    {activeSection === 'earn-transaction' && 'Track detailed earn transaction history'}
                    {activeSection === 'earn-points' && 'Manage earn debit by gift-card SKU date range'}
                    {activeSection === 'report' && 'Generate and view detailed reports'}
                    {activeSection === 'report-carb-cutt' && 'Generate Carb Cutt affiliate report by date range'}
                    {activeSection === 'report-general-affiliate' && 'Generate general affiliate report by date range'}
                    {activeSection === 'report-affiliate-payout' && 'Preview unpaid affiliates, allocate store credit, and release payout rows'}
                    {activeSection === 'knowledge-base' && 'Help documentation and FAQs'}
                    {activeSection === 'settings' && 'Configure your application preferences'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* <Button 
                  variant="outline" 
                  size="icon" 
                  className="relative"
                  onClick={() => setActiveSection('knowledge-base')}
                >
                  <BookOpen className="h-4 w-4" />
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                    3
                  </Badge>
                </Button> */}
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 min-w-0 overflow-auto bg-muted/30">
            <div
              className={cn(
                'mx-auto p-4 md:p-6',
                activeSection === 'ai-hub-fashion-design-prediction'
                  ? 'w-full max-w-none'
                  : 'container'
              )}
            >
              <SystemErrorAlert watchKey={activeSection} />
              {activeSection === 'dashboard' && (
                <DashboardPage onNavigate={setActiveSection} />
              )}
              {activeSection === 'gross-profit' && <GrossProfitPage />}
              {activeSection === 'earn-analytics' && <EarnAnalyticsPage />}
              {activeSection === 'vip-audit' && <VIPAuditPage />}
              {activeSection === 'vip-logger' && <VIPLoggerPage />}
              {activeSection === 'game-master-analytica' && <GameMasterAnalyticaPage />}
              {activeSection === 'knowledge-base' && <KnowledgeBasePage />}
              {activeSection === 'settings' && <Settings email={userEmail} />}
              {activeSection === 'product-management' && <ProductManagementPage />}
              {activeSection === 'brand-management' && <BrandManagementPage />}
              {activeSection === 'mbq-management' && <MBQManagementPage />}
              {activeSection === 'gift-card-events' && <GiftCardEventsPage />}
              {activeSection === 'free-gift-costs' && <FreeGiftCostPage />}
              {activeSection === 'ai-hub-fashion-design-prediction' && <FashionDesignPredictionPage />}
              {activeSection === 'overall-records' && <CustomerEarnPage />}
              {activeSection === 'earn-transaction' && <EarnTransactionPage />}
              {activeSection === 'earn-points' && <EarnPointsPage />}
              {activeSection === 'report' && <ReportPage />}
              {activeSection === 'report-carb-cutt' && <CarbCuttReportsPage />}
              {activeSection === 'report-general-affiliate' && <GeneralAffiliateReportsPage />}
              {activeSection === 'report-affiliate-payout' && <AffiliatePayOutPage />}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
